import { useState } from 'react'
import { useAuth } from 'react-oidc-context'

import { SystemSelector } from '@/components/features/system/SystemSelector'
import { ThemeToggle } from '@/components/ui/ThemeToggle'
import { type User } from '@/models/domain/user'
import { useAuthStore } from '@/stores/auth-store'
import { useThemeStore } from '@/stores/theme-store'

function MobileMenuButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="p-2 rounded-md text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 lg:hidden"
      aria-label="Toggle menu"
    >
      <svg
        className="h-6 w-6"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        aria-hidden="true"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M4 6h16M4 12h16M4 18h16"
        />
      </svg>
    </button>
  )
}

function HeaderBrand({ theme }: { theme: string }) {
  return (
    <div className="flex items-center gap-3">
      <img
        src={
          theme === 'dark'
            ? '/images/CloudSent-cloud-white-sm.png'
            : '/images/CloudSent-cloud-black-sm.png'
        }
        alt="CloudSent"
        width={58}
        height={28}
        className="shrink-0"
      />
      <h1 className="hidden md:block text-lg font-semibold tracking-tight text-gray-900 dark:text-gray-100">
        CloudSent Portal
      </h1>
    </div>
  )
}

function UserMenuDropdown({
  user,
  onLogout,
  onClose,
}: {
  user: User
  onLogout: () => void
  onClose: () => void
}) {
  return (
    <>
      <div
        className="fixed inset-0 z-10"
        role="button"
        tabIndex={0}
        onClick={onClose}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            onClose()
          }
        }}
        aria-label="Close user menu"
      />
      <div className="absolute right-0 mt-2 min-w-48 max-w-72 bg-white dark:bg-gray-800 rounded-md shadow-lg ring-1 ring-black ring-opacity-5 z-20">
        <div className="py-1">
          <div className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-gray-700">
            <p className="font-medium truncate" title={`${user.first_name} ${user.last_name}`}>
              {user.first_name} {user.last_name}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 truncate" title={user.email}>
              {user.email}
            </p>
          </div>
          <button
            onClick={onLogout}
            className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            Sign out
          </button>
        </div>
      </div>
    </>
  )
}

function fallbackLogout(logout: () => void) {
  logout()
  localStorage.removeItem('auth_token')
  localStorage.removeItem('auth_token_expiry')
  localStorage.removeItem('oidc.user:https://iam.svc.cloudsent.com/realms/corp:apicore-cli')
  sessionStorage.clear()
  window.location.href = '/login'
}

interface HeaderProps {
  onMenuClick: () => void
}

export function Header({ onMenuClick }: HeaderProps) {
  const auth = useAuth()
  const { user, logout } = useAuthStore()
  const { theme } = useThemeStore()
  const [showUserMenu, setShowUserMenu] = useState(false)

  const handleLogout = async () => {
    setShowUserMenu(false)

    try {
      const idToken = auth.user?.id_token
      await auth.removeUser()
      logout()

      if (idToken) {
        await auth.signoutRedirect({ id_token_hint: idToken })
        return
      }

      await auth.signoutRedirect()
    } catch (error) {
      console.error('OIDC logout failed:', error)
      fallbackLogout(logout)
    }
  }

  return (
    <header className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 sticky top-0 z-30">
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center gap-4">
            <MobileMenuButton onClick={onMenuClick} />
            <HeaderBrand theme={theme} />
          </div>

          <div className="flex items-center gap-2">
            <SystemSelector className="ml-2" />
            <ThemeToggle />

            <div className="relative">
              <button
                onClick={() => {
                  setShowUserMenu(!showUserMenu)
                }}
                className="flex items-center gap-2 p-2 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                <div className="h-8 w-8 rounded-full bg-primary-600 flex items-center justify-center text-white font-medium">
                  {user?.first_name.charAt(0).toUpperCase()}
                </div>
                <span className="hidden sm:block text-sm font-medium">
                  {user?.first_name} {user?.last_name}
                </span>
                <svg
                  className="hidden sm:block h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>

              {showUserMenu && user && (
                <UserMenuDropdown
                  user={user}
                  onLogout={handleLogout}
                  onClose={() => {
                    setShowUserMenu(false)
                  }}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </header>
  )
}
